import { createContext, FC, PropsWithChildren, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export interface TabsController {
  selectedIndex: number;
  goToTab: (index: number) => void;
  previous: () => void;
  next: () => void;
  urlParameter: string;
}

const TabsContext = createContext<TabsController>(undefined!);

type TabsProviderProps = PropsWithChildren<{
  urlParameterName?: string;
}>;

export const TabsProvider: FC<TabsProviderProps> = ({ children, urlParameterName }) => {
  const tab = urlParameterName ?? "tab";

  const [searchParams, setSearchParams] = useSearchParams();

  const getIndexFromURL = () => {
    const val = Number(searchParams.get(tab));
    return isNaN(val) ? 0 : val;
  };

  const [selectedIndex, setSelectedIndex] = useState(getIndexFromURL);

  // Sync state to URL change (e.g., on page load or back/forward nav)
  useEffect(() => {
    const urlIndex = getIndexFromURL();
    if (urlIndex !== selectedIndex) {
      setSelectedIndex(urlIndex);
    }
  }, [searchParams, tab]);

  const goToTab = (index: number) => {
    setSelectedIndex(index); // Immediate update

    // Use the functional setSearchParams form so this always merges into the latest
    // params instead of overwriting with a snapshot captured at render time — other
    // mounted tabs' own useListFilters instances can be writing their params concurrently.
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.set(tab, `${index}`);
        return nextParams;
      },
      { replace: false },
    );
  };

  const previous = () => goToTab(selectedIndex - 1);
  const next = () => goToTab(selectedIndex + 1);

  return (
    <TabsContext.Provider value={{ selectedIndex, goToTab, previous, next, urlParameter: tab }}>
      {children}
    </TabsContext.Provider>
  );
};

export const useTabsContext = (): TabsController => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("useTabs must be used within a TabsProvider");
  }
  return context;
};
